import React from "react";
import { cn } from "../../utils/cn";

// API antiga (com children)
interface TabsProps {
  value?: string;
  onValueChange?: (value: string) => void;
  children?: React.ReactNode;
  className?: string;
  // Nova API (com tabs array)
  tabs?: Array<{
    id: string;
    label: string;
    icon?: React.ReactNode;
    badge?: number | string;
    disabled?: boolean;
  }>;
  activeTab?: string;
  onTabChange?: (
    tabId: string,
  ) => void | React.Dispatch<React.SetStateAction<string>>;
  variant?: "default" | "pills" | "underline";
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  mobileLayout?: "scroll" | "grid";
}

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
}

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

// Componente Tabs principal - suporta ambas as APIs
export const Tabs: React.FC<TabsProps> = ({
  value,
  onValueChange,
  children,
  className = "",
  tabs,
  activeTab,
  onTabChange,
  variant = "default",
  size = "md",
  fullWidth = false,
  mobileLayout = "scroll",
}) => {
  // Se tabs array foi fornecido, usar a nova API
  if (tabs && activeTab !== undefined) {
    const currentTab = activeTab;
    const handleTabChange = (tabId: string) => {
      if (onTabChange) {
        if (typeof onTabChange === "function") {
          onTabChange(tabId);
        }
      }
    };

    const sizeClasses = {
      sm: "px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm min-h-[32px] sm:min-h-[36px]",
      md: "px-3.5 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-2.5 text-sm sm:text-sm md:text-base min-h-[36px] sm:min-h-[40px] md:min-h-[42px]",
      lg: "px-4 py-2.5 sm:px-5 sm:py-3 md:px-6 md:py-3 lg:px-7 lg:py-3.5 text-base sm:text-base md:text-lg min-h-[40px] sm:min-h-[44px] md:min-h-[48px]",
    };

    const variantClasses = {
      default: "border-b-2 border-transparent hover:border-purple-300",
      pills: "rounded-lg border",
      underline: "border-b-2 border-transparent hover:border-purple-200",
    };

    const activeClasses = {
      default: "border-purple-600 text-purple-600 bg-purple-50",
      pills: "bg-purple-100 text-purple-700 border-purple-300 shadow-xs",
      underline: "text-purple-700 font-bold bg-transparent border-purple-600",
    };

    const inactiveClasses = {
      default: "text-gray-500 hover:text-gray-700",
      pills:
        "bg-white text-gray-600 border-purple-300 hover:bg-gray-50 hover:border-purple-400 hover:text-gray-800 shadow-xs",
      underline: "text-gray-500 hover:text-purple-600 hover:bg-transparent",
    };

    return (
      <div
        className={cn(
          "flex",
          mobileLayout === "scroll"
            ? "overflow-x-auto overflow-y-hidden"
            : "w-full",
          variant === "underline" ? "bg-transparent" : "",
          variant === "pills" ? "bg-gray-100/50 p-1 rounded-xl" : "",
          className,
        )}
      >
        <nav
          className={cn(
            "flex gap-2",
            mobileLayout === "grid"
              ? "grid grid-cols-2 xs:grid-cols-3 sm:flex sm:flex-wrap md:flex-nowrap items-stretch w-full"
              : "flex-nowrap items-center",
            mobileLayout === "scroll" && "whitespace-nowrap min-w-max pr-8",
            variant === "underline" && "border-b border-purple-100 w-full",
          )}
        >
          {tabs.map((tab) => {
            const isActive = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => !tab.disabled && handleTabChange(tab.id)}
                onContextMenu={(e) => {
                  if (!tab.disabled) {
                    e.preventDefault();
                    handleTabChange(tab.id);
                  }
                }}
                disabled={tab.disabled}
                className={cn(
                  "inline-flex items-center font-bold transition-all duration-300 relative group",
                  "focus:outline-hidden rounded-lg",
                  "active:scale-95",
                  sizeClasses[size],
                  variant === "pills"
                    ? "justify-center py-2.5 text-[10px] sm:text-[11px] uppercase tracking-wide border"
                    : variantClasses[variant],
                  isActive ? activeClasses[variant] : inactiveClasses[variant],
                  tab.disabled && "opacity-30 cursor-not-allowed",
                  (fullWidth || mobileLayout === "grid") &&
                    "flex-1 justify-center",
                  variant === "underline" &&
                    "px-0! rounded-none! pt-1.5 pb-2.5 border-b-[3px] transition-colors",
                  variant === "underline" && isActive
                    ? "border-purple-600 bg-purple-50/60"
                    : "border-transparent",
                  variant === "underline" &&
                    "uppercase tracking-widest text-[10px] md:text-[11px]",
                )}
              >
                {tab.icon && (
                  <span
                    className={cn(
                      "mr-1.5 sm:mr-2 shrink-0 transition-opacity",
                      isActive ? "opacity-100" : "opacity-70",
                    )}
                  >
                    {tab.icon}
                  </span>
                )}
                <span
                  className={cn(
                    mobileLayout === "grid"
                      ? "text-center line-clamp-1"
                      : "whitespace-nowrap",
                  )}
                >
                  {tab.label}
                </span>
                {tab.badge !== undefined && (
                  <span
                    className={cn(
                      "ml-1.5 sm:ml-2 px-1.5 sm:px-2 py-0.5 rounded-full text-xs sm:text-sm font-semibold",
                      isActive
                        ? "bg-purple-600 text-white"
                        : "bg-gray-200 text-gray-700",
                    )}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    );
  }

  // API antiga com children
  return (
    <div className={className} data-tabs-value={value}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            activeValue: value,
            onValueChange,
          } as any);
        }
        return child;
      })}
    </div>
  );
};

export const TabsList: React.FC<
  TabsListProps & {
    activeValue?: string;
    onValueChange?: (value: string) => void;
  }
> = ({ children, className = "", activeValue, onValueChange }) => {
  return (
    <div className={`flex space-x-1 ${className}`}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            activeValue,
            onValueChange,
          } as any);
        }
        return child;
      })}
    </div>
  );
};

export const TabsTrigger: React.FC<
  TabsTriggerProps & {
    activeValue?: string;
    onValueChange?: (value: string) => void;
  }
> = ({
  value,
  children,
  disabled = false,
  className = "",
  activeValue,
  onValueChange,
}) => {
  const isActive = activeValue === value;

  return (
    <button
      type="button"
      onClick={() => !disabled && onValueChange && onValueChange(value)}
      onContextMenu={(e) => {
        if (!disabled && onValueChange) {
          e.preventDefault();
          onValueChange(value);
        }
      }}
      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
        disabled
          ? "text-gray-400 cursor-not-allowed"
          : isActive
            ? "text-purple-600 bg-purple-50 border-b-2 border-purple-600"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
      } ${className}`}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

export const TabsContent: React.FC<
  TabsContentProps & { activeValue?: string }
> = ({ value, children, className = "", activeValue }) => {
  if (activeValue !== value) {
    return null;
  }

  return <div className={className}>{children}</div>;
};
